import { Component, OnInit, OnDestroy, inject, ElementRef, AfterViewInit, computed, signal, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HeaderLogoComponent } from '../header-logo/header-logo.component';
import { HeaderNavigationalButtonComponent } from '../header-navigational-button/header-navigational-button.component';
import { OrderDetailsButtonComponent } from '../order-details-button/order-details-button.component';
import { AccountButtonComponent } from '../account-button/account-button.component';
import { ViewOrderButtonComponent } from '../view-order-button/view-order-button.component';
import { IconComponent } from '../icons/icon.component';
import { IconNameType } from '../icons/icon-name-type.enum';
import { RoundIconType } from '../round-icon/round-icon-type.enum';
import { slideInLeft } from '../animations/animations';
import { LayoutService } from '../../core/services/layout.service';
import { OrderService } from '../../core/services/order.service';
import { Router } from '@angular/router';
import { StoreIdService } from '../../core/services/store-id.service';
import { ConfigService } from '../../core/services/config.service';
import { ViewOrderButtonRoundComponent } from '../view-order-button-round/view-order-button-round.component';
import { ModalService, ModalName } from '../../core/services/modal.service';
import { TranslocoDirective } from '@ngneat/transloco';
import { FeaturedContentService } from '../../core/services/featured-content.service';
import { FeaturedContent } from '../../core/models/core/featured-content.model';
import { FeaturedContentName } from '../../core/models/core/featured-content-name.enum';
import { InitParamsService } from '../../core/services/init-params.service';
import { StoreInfoComponent } from "../store-info/store-info.component";
import { PremiumService } from '../../loyalty/premium/premium.service';

@Component({
  selector: 'cwo-header-layout',
  standalone: true,
  imports: [
    CommonModule,
    HeaderLogoComponent,
    HeaderNavigationalButtonComponent,
    OrderDetailsButtonComponent,
    AccountButtonComponent,
    ViewOrderButtonComponent,
    ViewOrderButtonRoundComponent,
    IconComponent,
    TranslocoDirective,
    StoreInfoComponent
],
  templateUrl: './header-layout.component.html',
  styleUrl: './header-layout.component.scss',
  animations: [slideInLeft()],
})
export class HeaderLayoutComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly header = viewChild('header', { read: ElementRef });
  readonly headerPrimaryGroup = viewChild('headerPrimaryGroup', { read: ElementRef });
  readonly headerSecondaryGroup = viewChild('headerSecondaryGroup', { read: ElementRef });
  readonly mainNavigation = viewChild('mainNavigation', { read: ElementRef });

  layout = inject(LayoutService);
  modalService = inject(ModalService);
  configService = inject(ConfigService);
  initParamsService = inject(InitParamsService);
  isMobile = signal(false);

  private featuredContentService = inject(FeaturedContentService);
  headerFeaturedContentLinks: FeaturedContent[] = [];

  openCart() {
    this.modalService.toggleModal(ModalName.Cart, undefined, true);
  }

  loyalty = this.configService.loyaltyState();
  store = this.configService.configState().store;


  premiumService = inject(PremiumService);

  //account menu button flags
  isLoggedIn = false;
  accountMenuExpanded = false;
  iconNameType = IconNameType;
  roundIconType = RoundIconType;
  orderService = inject(OrderService);

  storeIdService = inject(StoreIdService);

  order = this.orderService.order;
  isSelfOrder = computed(() => this.order().isInitialized && this.order().isSelfOrderSource);
  orderTypeLabel = computed(() => {
    const orderType = this.order().orderType;
    return this.configService.findOrderTypeConfig(orderType)?.displayName ?? orderType;
  });
  isCheckout = false;
  private resizeObserver?: ResizeObserver;
  shouldForceHideNavigation = false;
  private MIN_SPACING = 120;
  private DESKTOP_BREAKPOINT = 1200;
  private navNaturalWidth = 0;

  private router = inject(Router);

  constructor() {
    if (this.initParamsService.isEmbedded) this.isMobile.set(false);
    else this.isMobile.set(this.layout.mobileView());
  }

  ngOnInit(): void {
    this.isCheckout = this.router.url.includes('/checkout');

    this.router.events.subscribe(() => {
      this.isCheckout = this.router.url.includes('/checkout');
    });
    this.headerFeaturedContentLinks = this.featuredContentService.getFeaturedContentsByAreaName(FeaturedContentName.HEADER_LINKS);
  }

  ngAfterViewInit(): void {
    this.resizeObserver = new ResizeObserver(() => this.checkNavigationOverflow());
    [this.header(), this.headerPrimaryGroup(), this.headerSecondaryGroup(), this.mainNavigation()]
      .forEach(el => el?.nativeElement && this.resizeObserver!.observe(el.nativeElement));
    setTimeout(() => this.checkNavigationOverflow(), 0);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  checkNavigationOverflow(): void {
    const headerEl = this.header()?.nativeElement as HTMLElement | undefined;
    const primaryEl = this.headerPrimaryGroup()?.nativeElement as HTMLElement | undefined;
    const secondaryEl = this.headerSecondaryGroup()?.nativeElement as HTMLElement | undefined;
    const navEl = this.mainNavigation()?.nativeElement as HTMLElement | undefined;
    const elementsMissing = !headerEl || !primaryEl || !secondaryEl || !navEl;
    if (elementsMissing) {
      return;
    }

    if (window.innerWidth < this.DESKTOP_BREAKPOINT) {
      this.shouldForceHideNavigation = false;
      return;
    }

    if (!this.shouldForceHideNavigation) {
      this.navNaturalWidth = navEl.scrollWidth;
    }

    const headerStyles = getComputedStyle(headerEl);
    const horizontalPadding = parseFloat(headerStyles.paddingLeft) + parseFloat(headerStyles.paddingRight);
    const available = headerEl.clientWidth - horizontalPadding;

    const logoWidth = this.shouldForceHideNavigation
      ? primaryEl.scrollWidth
      : primaryEl.scrollWidth - this.navNaturalWidth;

    const required = logoWidth + this.navNaturalWidth + secondaryEl.scrollWidth + this.MIN_SPACING;

    this.shouldForceHideNavigation = required > available;
  }

  toggleLoggedIn() {
    this.isLoggedIn = !this.isLoggedIn;
  }

  accountButtonCallback() {
    this.accountMenuExpanded = !this.accountMenuExpanded;
  }

  openMobileSidebar() {
    this.modalService.toggleModal(ModalName.MobileSideNav, undefined, true);
  }
}
